import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DEFAULT_HERO_ELEMENTS, DEFAULT_HERO_BUTTONS_ORDER, DEFAULT_HERO_LAYOUT_ALIGN } from '../components/hero/heroLayoutDefaults';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SiteSettingsContext = createContext(null);

// Maps the display names offered in the font picker to their fonts.bunny.net slug.
const FONT_SLUGS = {
  'Manrope': 'manrope',
  'Inter': 'inter',
  'Poppins': 'poppins',
  'DM Sans': 'dm-sans',
  'Space Grotesk': 'space-grotesk',
  'Outfit': 'outfit',
  'Plus Jakarta Sans': 'plus-jakarta-sans',
};

// Fonts that are self-hosted (see @font-face in index.css) instead of loaded
// from fonts.bunny.net — no external stylesheet link needed for these.
const LOCAL_FONTS = ['BMW'];

export const useSiteSettings = () => useContext(SiteSettingsContext);

const DEFAULTS = {
  business_info: {
    company_name: 'CHROBE One World',
    legal_form: '',
    owner_name: '',
    vat_id: '',
    tax_number: '',
    commercial_register: '',
    street: 'Am Hirtenhaus 17',
    zip: '66571',
    city: 'Eppelborn',
    country: 'Deutschland',
    phone: '06881/5959 023',
    mobile: '',
    email: 'info@chrobe.de',
    website: 'https://chrobe.de',
  },
  design: {
    primary_color: '#0094ff',
    primary_hover: '#33a9ff',
    background: '#161313',
    surface: '#231f1f',
    text_primary: '#ffffff',
    text_secondary: '#a1a1aa',
    accent_muted: '#605e5e',
    font_family: 'Manrope',
    button_radius: '0.5rem',
  },
  pages: {
    landing: {
      hero_title: 'Glanz, den man sieht. Schutz, den man nicht sieht.', hero_title_accent: 'Fahrzeugaufbereitung im Saarland, mobil oder an unseren Standorten', hero_description: 'Innen- und Außenaufbereitung, Lackpolitur und Keramikversiegelung für Privatkunden, Autohäuser und Flotten. Vorab erhältst du ein schriftliches Festpreis-Angebot.', cta_primary: 'Unsere Services', cta_secondary: 'Termin anfragen',
      hero_elements: DEFAULT_HERO_ELEMENTS,
      hero_buttons_order: DEFAULT_HERO_BUTTONS_ORDER,
      hero_layout_align: DEFAULT_HERO_LAYOUT_ALIGN,
      hero_free_positions: {},
    },
    about: { title: 'Über CHROBE One World', subtitle: 'Deine Premium-Fahrzeugpflege im Saarland', content: '' },
    services: { title: 'Unsere Services', subtitle: 'Professionelle Fahrzeugpflege auf höchstem Niveau' },
    products: { title: 'Premium Pflegeprodukte', subtitle: 'Ausgewählte Produkte für anspruchsvolle Fahrzeugpflege' },
    contact: { title: 'Kontakt', subtitle: 'Wir sind für dich da', phone: '06881/5959 023', email: 'info@chrobe.de', address: 'Saarland, Deutschland' },
  },
  footer: { description: 'CHROBE One World — dein Premium-Partner für mobile Fahrzeugpflege, Detailing und Veredelung im Saarland.', phone: '06881/5959 023', email: 'info@chrobe.de', address: 'Saarland, Deutschland', copyright: 'CHROBE One World. Alle Rechte vorbehalten.' },
  social: { instagram: '', whatsapp: '4968815959023' },
  opening_hours: {
    monday: '08:00 - 18:00',
    tuesday: '08:00 - 18:00',
    wednesday: '08:00 - 18:00',
    thursday: '08:00 - 18:00',
    friday: '08:00 - 20:00',
    saturday: '08:00 - 20:00',
    sunday: 'Geschlossen',
  },
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/site-settings/public`);
      setSettings(res.data);
    } catch (e) {
      console.error('SiteSettings laden fehlgeschlagen:', e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Apply CSS variables based on design settings
  useEffect(() => {
    if (!settings?.design) return;
    const d = settings.design;
    const root = document.documentElement;
    root.style.setProperty('--primary-custom', d.primary_color);
    root.style.setProperty('--primary-hover-custom', d.primary_hover);
    root.style.setProperty('--bg-custom', d.background);
    root.style.setProperty('--surface-custom', d.surface);
    root.style.setProperty('--text-primary-custom', d.text_primary);
    root.style.setProperty('--text-secondary-custom', d.text_secondary);
    root.style.setProperty('--accent-muted-custom', d.accent_muted);
    root.style.setProperty('--btn-radius-custom', d.button_radius);
    if (d.font_family) {
      root.style.setProperty('--font-custom', `'${d.font_family}'`);
      const slug = FONT_SLUGS[d.font_family];
      if (slug && !LOCAL_FONTS.includes(d.font_family)) {
        let link = document.getElementById('dynamic-font-link');
        if (!link) {
          link = document.createElement('link');
          link.id = 'dynamic-font-link';
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
        link.href = `https://fonts.bunny.net/css?family=${slug}:300,400,500,600,700,800&display=swap`;
      }
    }
  }, [settings]);

  const getPage = (key) => settings?.pages?.[key] || DEFAULTS.pages[key] || {};
  const getDesign = () => settings?.design || DEFAULTS.design;
  const getFooter = () => settings?.footer || DEFAULTS.footer;
  const getSocial = () => settings?.social || DEFAULTS.social;
  const getOpeningHours = () => settings?.opening_hours || DEFAULTS.opening_hours;
  const getBusinessInfo = () => ({ ...DEFAULTS.business_info, ...(settings?.business_info || {}) });
  const getNav = () => settings?.nav || {};

  return (
    <SiteSettingsContext.Provider value={{ settings, loaded, fetchSettings, getPage, getDesign, getFooter, getSocial, getOpeningHours, getBusinessInfo, getNav }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export default SiteSettingsContext;
